import React from 'react'
import { translate } from 'react-i18next'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import { Label, Button, ErrorText, Stack } from '../components'
import * as t from '../services/translationService'
import { resendVerificationMail, confirmVerifyEmail, updateUser, userInfoError } from '../actions/AuthAction'
import PropTypes from 'prop-types'
import AuthContent from './AuthContent'
import * as networkService from '../services/networkService'
import * as routeService from '../services/routeService'
import { getApiErrorMessage } from '../utils/apiError'
import styles2 from 'assets/scss/views/Auth2.scss'

const COUNT_LIMIT = 60

class VerifyEmailView extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      counter: COUNT_LIMIT,
      showError: false,
      errorMessage: '',
    }
    this.timer = null
  }

  static propTypes = {
    user: PropTypes.any,
    location: PropTypes.any,
    phaseInformationError: PropTypes.any,
    applicationProfileError: PropTypes.any,
    resendVerificationMail: PropTypes.func,
    confirmVerifyEmail: PropTypes.func,
    updateUser: PropTypes.func,
    userInfoError: PropTypes.func,
  }

  componentDidMount() {
    this.startCountdown()
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  startCountdown = () => {
    this.timer = setInterval(() => {
      this.setState(prevState => {
        if (prevState.counter > 0) {
          return { counter: prevState.counter - 1 }
        } else {
          clearInterval(this.timer)
          return null
        }
      })
    }, 1000)
  }

  resetCountdown = () => {
    this.setState({ counter: COUNT_LIMIT }, () => this.startCountdown())
  }

  handleResendEmail = async () => {
    const { user, resendVerificationMail } = this.props
    const { unverifiedEmail } = user

    this.resetCountdown()
    resendVerificationMail(unverifiedEmail)
  }

  handleConfirm = async (userId, token) => {
    const {
      location: { state },
    } = this.props

    const user = await this.props.confirmVerifyEmail(userId, token)
    if (user.email && !user.unverifiedEmail) {
      if (state && state.from) {
        routeService.goToPage(state.from)
      } else {
        routeService.goToRegisterPage({ replace: true })
      }
    } else {
      this.setState({ showError: true, errorMessage: t.verifyEmailNotDone() })
    }
  }

  requestToken = () => {
    const { user, updateUser, userInfoError } = this.props
    const { id: userId, unverifiedEmailRequestToken } = user
    return networkService
      .requestAccessTokenFromEmail(userId, unverifiedEmailRequestToken)
      .then(response => {
        // v2 wraps the payload in { data, debugInfo }; unwrap .data.data to the flat session.
        // Defaulted to {} because destructuring throws on a null/undefined right-hand side,
        // unlike a plain property read -- a body that is not the envelope has to end up in
        // the "not verified" branch below instead of throwing past the catch.
        const { accessToken, ...rest } = (response.data && response.data.data) || {}
        if (accessToken) {
          const information = { token: accessToken, isAuthenticated: true, accessToken, ...rest }
          this.handleConfirm(userId, accessToken)
          updateUser(information)
        } else {
          this.setState({ showError: true, errorMessage: t.verifyEmailNotDone() })
        }
      })
      .catch(err => {
        // v2 error bodies carry `exception`, not `error`, so read the message through the
        // shared helper rather than reaching for a key that is no longer there.
        const jsonData = err.response && err.response.data
        // The helper returns undefined when there is no response body at all (a timeout, a
        // refused connection), and the error line renders whatever is in state -- so without
        // a fallback the user gets an empty error. Same message the other failure paths use.
        this.setState({ showError: true, errorMessage: getApiErrorMessage(err) || t.verifyEmailNotDone() })
        userInfoError(jsonData)
        return Promise.reject(jsonData)
      })
  }

  handleVerify = async () => {
    this.setState({ showError: false, errorMessage: '' })
    const { user } = this.props
    const {
      id: userId,
      email,
      unverifiedEmail,
      token,
      accessToken,
      unverifiedPhoneIsVerifiable,
      unverifiedEmailRequestToken,
    } = user
    if (token || accessToken) {
      this.handleConfirm(userId, token || accessToken)
      return
    }

    if (!email && unverifiedEmail && !unverifiedPhoneIsVerifiable && unverifiedEmailRequestToken && !token) {
      this.requestToken()
      return
    }

    if (!token && !unverifiedEmailRequestToken) {
      this.setState({ showError: true, errorMessage: t.verifyEmailNotDone() })
    }
  }

  render() {
    const { phaseInformationError, applicationProfileError, user } = this.props

    const showCounter = this.state.counter > 0

    return (
      <React.Fragment>
        <AuthContent title={t.verifyEmailTitle()} hasToken={false}>
          {(phaseInformationError || applicationProfileError) && <ErrorText>{t.generalError()}</ErrorText>}
          <div>
            <Label>{t.verifyEmailMessage(user.unverifiedEmail)}</Label>
            <div className={styles2.Spacer8} />
            <Button onClick={this.handleVerify} fullWidth={true} primary={true}>
              {t.verifyEmailDone()}
            </Button>
          </div>
          {this.state.showError && <ErrorText>{this.state.errorMessage}</ErrorText>}
          <div className={styles2.Spacer8} />
          <Stack spacing="tight">
            <Label>{t.verifyEmailNotRecievedMessage()}</Label>
            <Button plain={true} onClick={this.handleResendEmail} disabled={showCounter}>
              {t.verifyEmailResend()}
              {showCounter && ` (${this.state.counter})`}
            </Button>
          </Stack>
        </AuthContent>
      </React.Fragment>
    )
  }
}

const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {}, error } = AuthReducer
  const { phaseInformationError, applicationProfileError } = FlowReducer
  return {
    error,
    user,
    phaseInformationError,
    applicationProfileError,
  }
}

const mapDispatchToProps = dispatch => ({
  resendVerificationMail: email => dispatch(resendVerificationMail(email)),
  confirmVerifyEmail: (userId, token) => dispatch(confirmVerifyEmail(userId, token)),
  updateUser: user => dispatch(updateUser(user)),
  userInfoError: errorData => dispatch(userInfoError(errorData)),
})

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(VerifyEmailView)



// WEBPACK FOOTER //
// ./src/views/VerifyEmailView.js